// emailApi.ts — thin typed wrappers around the email-flow endpoints
// introduced in middleware Phase 1-5.
//
// These functions return the raw envelope so callers can branch on the
// numeric `code` (e.g. lockout=18, mailer-down=31, no-email=30). They
// do NOT auto-refresh redux — callers fire `store.getLogin()` after
// flows that mutate the descriptor (verify, confirm_change).
//
// Anti-enumeration note (password_reset.request): the response body
// is byte-identical for known / unknown / unverified / throttled
// emails. The FE MUST NOT surface anything that distinguishes them.

import { apiOperation } from '@/app/api/common';

export interface EmailEnvelopeError {
  code:				number;
  error:			string;
}

export interface EmailEnvelope {
  errors:			EmailEnvelopeError[];
}

export interface VerifyEmailCodeResponse extends EmailEnvelope {
  verify_token?:	string;
  expires_at?:		string;
}

// emailCode envelope normalisation — apiOperation returns
// `{response, result}`. result is null on transport failure;
// otherwise result.errors[0] carries (code, error). This helper
// guards against the null-result case so callers don't have to.
//
// Typed as unknown + narrowed because apiOperation's `result` is
// untyped JSON parsed off the wire; we cannot rely on its shape
// without a runtime check.
function firstErr(env: unknown): EmailEnvelopeError {
  if (env && typeof env === 'object' && 'errors' in env) {
    const errs = (env as { errors?: EmailEnvelopeError[] }).errors;
    if (Array.isArray(errs) && errs.length > 0 && errs[0]) {
      return errs[0];
    }
  }
  return { code: -1, error: "request failed" };
}

// ============================================================================
// /email.json (authenticated)
// ============================================================================

export async function startVerifyEmail(): Promise<EmailEnvelopeError> {
  const data = await apiOperation("POST", "/email.json?action=start_verify", {});
  return firstErr(data.result);
}

export async function verifyEmail(code: string): Promise<EmailEnvelopeError> {
  const data = await apiOperation("POST", "/email.json?action=verify", { code });
  return firstErr(data.result);
}

export async function requestEmailChange(newEmail: string, currentPassword: string): Promise<EmailEnvelopeError> {
  const data = await apiOperation("POST", "/email.json?action=request_change", {
    new_email:			newEmail,
    current_password:	currentPassword,
  });
  return firstErr(data.result);
}

export async function confirmEmailChange(code: string): Promise<EmailEnvelopeError> {
  const data = await apiOperation("POST", "/email.json?action=confirm_change", { code });
  return firstErr(data.result);
}

export async function cancelEmailChange(): Promise<EmailEnvelopeError> {
  const data = await apiOperation("POST", "/email.json?action=cancel_change", {});
  return firstErr(data.result);
}

// ============================================================================
// /change_password.json (authenticated) — email gate preflight
// ============================================================================

export async function requestChangePasswordCode(email: string): Promise<EmailEnvelopeError> {
  const data = await apiOperation("POST", "/change_password.json?action=request_email_code",
    { email }
  );
  return firstErr(data.result);
}

// Returns the full envelope so callers can read verify_token + expires_at.
export async function verifyChangePasswordCode(code: string): Promise<VerifyEmailCodeResponse> {
  const data = await apiOperation("POST", "/change_password.json?action=verify_email_code",
    { code }
  );
  if (!data.result) {
    return { errors: [{ code: -1, error: "request failed" }] };
  }
  return data.result as VerifyEmailCodeResponse;
}

// ============================================================================
// /logins.json?action=change_password (authenticated, EXISTING — extended)
// /local_users.json?...&action=change_password — same body
// ============================================================================

export interface ChangePasswordBody {
  current_password:		string;
  new_password:			string;
  verify_token?:		string;   // OPTIONAL today (compat shim);
                                  // REQUIRED after operator flips
                                  // allowMissingVerifyToken to false.
}

export async function changePasswordGlobal(body: ChangePasswordBody): Promise<EmailEnvelopeError> {
  const data = await apiOperation("POST", "/logins.json?action=change_password", body);
  return firstErr(data.result);
}

export async function changePasswordLocal(projectId: string, body: ChangePasswordBody): Promise<EmailEnvelopeError> {
  const url = `/local_users.json?project_id=${encodeURIComponent(projectId)}&action=change_password`;
  const data = await apiOperation("POST", url, body);
  return firstErr(data.result);
}

// ============================================================================
// /password_reset.json (UNAUTHENTICATED — forgot password)
// ============================================================================

// Wire contract: POST only, ?action=request | ?action=confirm, NO session,
// Origin-checked (must be same-origin). The endpoint ALWAYS returns the
// standard envelope — even on 403/404/429/503 — and the FE must branch on
// the HTTP STATUS, not just errors[0].code.
//
// These deliberately use a RAW fetch instead of apiOperation: on a non-2xx
// apiOperation consumes the body (response.text()), returns result:null, and
// raises a GLOBAL error toast. This flow needs the envelope on 404/429/503
// and surfaces it INLINE in the reset form instead.

const RESET_URL = "/password_reset.json";

interface ResetReply {
  status:		number;
  code:			number;
  error:		string;
  retryAfter:	number;
}

async function postReset(action: string, body: any): Promise<ResetReply> {
  const response = await fetch(`${RESET_URL}?action=${action}`, {
    method:		"POST",
    headers:	{ "Content-Type": "application/json" },
    body:		JSON.stringify(body),
  });

  // Only meaningful on the 429 throttle.
  const ra = parseInt(response.headers.get("Retry-After") || "", 10);

  let code = -1;
  let error = "";
  try {
    const first = firstErr(await response.json());
    code = first.code;
    error = first.error;
  } catch (_e) {
    // 405 / "email service not initialised" / unknown-action reply in plain
    // text rather than the envelope.
    error = response.statusText;
  }

  return { status: response.status, code, error, retryAfter: isNaN(ra) ? 0 : ra };
}

// requestPasswordReset verifies the username+email pair and, on a real match,
// emails a 6-digit code + a {base}/reset?token=… link (one shared ticket,
// 30-min TTL).
//
// ANTI-ENUMERATION: the 404 (code 39) is deliberately IDENTICAL for unknown
// username / wrong email / unverified email / unknown-or-disabled project.
// Surface errors[0].error verbatim — never tell the user which field was wrong.
//
// projectId applies to local (project-scoped) users only; global users omit it.
// NOTE: username is REQUIRED — the old email-only body now 404s for everyone.
export type ResetRequestOutcome =
  | { kind: "sent";			message: string }
  | { kind: "no_match";		message: string }
  | { kind: "rate_limited";	message: string; retryAfter: number }
  | { kind: "unavailable";	message: string }
  | { kind: "failure";		message: string };

export async function requestPasswordReset(
  username: string,
  email: string,
  projectId?: string,
): Promise<ResetRequestOutcome> {
  const body: { username: string; email: string; project_id?: string } = {
    username:	username.trim(),
    email:		email.trim(),
  };
  if (projectId && projectId.trim()) body.project_id = projectId.trim();

  const r = await postReset("request", body);

  if (r.status === 200 && r.code === 0) return { kind: "sent", message: r.error };
  if (r.status === 404) return { kind: "no_match", message: r.error };
  if (r.status === 429) return { kind: "rate_limited", message: r.error, retryAfter: r.retryAfter };
  if (r.status === 503) return { kind: "unavailable", message: r.error };
  return { kind: "failure", message: r.error };
}

// confirmPasswordReset* redeems the code/link and sets the new password. On
// success the backend revokes ALL of the user's sessions — the caller must
// route to the login screen, never treat the user as signed in.
//
// Confirm failures ARE specific (the caller already holds a code/token, so
// there's nothing to hide).
export type ResetConfirmOutcome =
  | { kind: "ok";				message: string }
  | { kind: "invalid_code";		message: string }	// code 8 — invalid/expired/used
  | { kind: "weak_password";	message: string }	// code 5 — message IS the reason
  | { kind: "failure";			message: string };

function toConfirmOutcome(r: ResetReply): ResetConfirmOutcome {
  if (r.status === 200 && r.code === 0) return { kind: "ok", message: r.error };
  if (r.code === 8) return { kind: "invalid_code", message: r.error };
  if (r.code === 5) return { kind: "weak_password", message: r.error };
  return { kind: "failure", message: r.error };
}

// Token form — used by the /reset?token=… landing. Self-contained: the link
// carries the scope, so no project_id is needed.
export async function confirmPasswordResetByToken(
  token: string,
  newPassword: string,
): Promise<ResetConfirmOutcome> {
  return toConfirmOutcome(await postReset("confirm", {
    token:			token,
    new_password:	newPassword,
  }));
}

// Typed-code form — project_id is REQUIRED for a local user (the reset-code
// key is project-scoped), ignored for global users.
export async function confirmPasswordResetByCode(
  email: string,
  code: string,
  newPassword: string,
  projectId?: string,
): Promise<ResetConfirmOutcome> {
  const body: { email: string; code: string; new_password: string; project_id?: string } = {
    email:			email.trim(),
    code:			code.trim(),
    new_password:	newPassword,
  };
  if (projectId && projectId.trim()) body.project_id = projectId.trim();
  return toConfirmOutcome(await postReset("confirm", body));
}
